// src/utils/scopeCss.js
//
// The superadmin/*.jsx pages (SalesWorkspace, PipelineBoard,
// AiPerformanceDashboard, RevenueForecastDashboard) were each built as
// standalone full-page routes: they inject their own <style> block with
// plain, unscoped selectors (.dash-root, .dash-header, .btn, :root, ...).
// That's fine as a full-page route, but Phase 5 now also renders these
// same components *embedded* inside a tab on CustomerDashboard, which
// already defines its own (different) rules for those exact same class
// names. Without scoping, whichever <style> tag mounts last wins and
// bleeds into the other component's markup.
//
// scopeCss rewrites a plain CSS string so every top-level selector is
// prefixed with a wrapper scope (e.g. ".ri-embed-sales"), so the rules
// only ever apply inside that embedded instance. It understands:
//   - plain rules:          .foo, .bar { ... }
//   - :root:                rewritten to the scope itself, since ":root"
//                            can't be prefixed as a descendant
//   - @media blocks:        recursed into, wrapper kept as-is
//   - other @ rules
//     (@keyframes, @font-face): left untouched, their inner selectors
//                            are percentages/identifiers, not elements
export function scopeCss(css, scope) {
  return rewrite(css, scope);
}

function rewrite(css, scope) {
  let out = "";
  let i = 0;
  const n = css.length;

  while (i < n) {
    // Skip whitespace/comments between rules
    const wsMatch = /^\s+/.exec(css.slice(i));
    if (wsMatch) {
      out += wsMatch[0];
      i += wsMatch[0].length;
      continue;
    }
    if (css.slice(i, i + 2) === "/*") {
      const end = css.indexOf("*/", i + 2);
      const stop = end === -1 ? n : end + 2;
      out += css.slice(i, stop);
      i = stop;
      continue;
    }
    if (i >= n) break;

    // Find the selector/prelude up to the next '{'
    const braceIdx = css.indexOf("{", i);
    if (braceIdx === -1) {
      out += css.slice(i);
      break;
    }
    const prelude = css.slice(i, braceIdx);
    const trimmedPrelude = prelude.trim();

    // Find the matching closing brace for this block
    const blockEnd = findMatchingBrace(css, braceIdx);
    const body = css.slice(braceIdx + 1, blockEnd);

    if (
      trimmedPrelude.startsWith("@media") ||
      trimmedPrelude.startsWith("@supports")
    ) {
      out += `${prelude}{${rewrite(body, scope)}}`;
    } else if (trimmedPrelude.startsWith("@")) {
      // @keyframes, @font-face, @import, etc, leave untouched
      out += `${prelude}{${body}}`;
    } else {
      out += scopeSelectors(prelude, scope) + `{${body}}`;
    }

    i = blockEnd + 1;
  }

  return out;
}

function scopeSelectors(prelude, scope) {
  const selectors = splitTopLevel(prelude, ",");
  const scoped = selectors.map((sel) => {
    const s = sel.trim();
    if (!s) return s;
    if (s === ":root") return scope;
    if (s.startsWith("*")) return `${scope} ${s}`;
    return `${scope} ${s}`;
  });
  return scoped.join(", ");
}

// Splits a string on a delimiter, but only at depth 0 (ignores commas
// inside parens, e.g. :not(a, b))
function splitTopLevel(str, delim) {
  const parts = [];
  let depth = 0;
  let current = "";
  for (const ch of str) {
    if (ch === "(") depth++;
    if (ch === ")") depth--;
    if (ch === delim && depth === 0) {
      parts.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  parts.push(current);
  return parts;
}

function findMatchingBrace(css, openIdx) {
  let depth = 0;
  for (let j = openIdx; j < css.length; j++) {
    if (css[j] === "{") depth++;
    else if (css[j] === "}") {
      depth--;
      if (depth === 0) return j;
    }
  }
  return css.length - 1;
}
